import 'core-js';
import 'whatwg-fetch';
import React, { useEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';
import { SToastViewport } from '@chargebee/sting-react';
import { type APIError } from '@bbkAdminRedux/rtkq/rtkq-error-logger';
import { detectIE } from '@bbkAdminUtils/detect-ie';
import { renderInlineToDOM } from '@bbkAdminUtils/render_client_side_funnel';
import { FunnelContext } from '@bbkFunnel/components/FunnelContext';
import {
  createEmbedBridge,
  hostedUrl,
  readEmbedParams,
} from '@bbkFunnel/embed/embed-bridge';
import { navigateInThisWindow } from '@bbkFunnel/events';
import Funnel from '@bbkFunnel/pages/Funnel';
import {
  type FailedExperienceResponse,
  type RuntimeExperienceResponse,
} from '@bbkFunnelComponents/funnel-types';
import {
  getPageLoadModalCardTheme,
  resolveFunnelTheme,
} from '@bbkFunnelUtils/funnel-utils';
import { PricingPageShimmerLoader } from '@bbkPricingPage/components/loader/pricing-page-shimmer-loader';
import { defaultMUiTheme } from '@bbkShared/material-ui-theme';
import { matchPath } from '@bbkShared/react-router';

/**
 * How long an embedded run waits for its experience before giving up and
 * escaping to the hosted page. The parent only bounds the `ready` handshake,
 * and that has already been answered by the time this fetch is in flight.
 */
const EMBEDDED_LOAD_TIMEOUT_MS = 15000;

const FunnelLoader: React.FC = () => {
  const [response, setResponse] = useState<RuntimeExperienceResponse>();
  const [isPricingPage, setIsPricingPage] = useState<boolean>(false);
  const [pricingPageSessionId, setPricingPageSessionId] = useState<
    string | undefined
  >();
  const [error, setError] = useState<string>();
  const [pricingPageKey, setPricingPageKey] = useState<string | undefined>();
  const [legacySiteId, setLegacySiteId] = useState<string | undefined>();
  const [legacyPricingPageId, setLegacyPricingPageId] = useState<
    string | undefined
  >();
  const [defaultCurrency, setDefaultCurrency] = useState<string | undefined>();

  const theme = createTheme(defaultMUiTheme);

  // Extract host from URL params at component level
  const params = new URLSearchParams(window.location.search);
  const host = params.get('host') || undefined;
  const variantId = params.get('variant-id') || undefined;

  /**
   * Embedding changes nothing about the experience, only who performs its
   * top-level navigations. Resolved once so the same bridge instance is handed
   * to every `link` sync for the life of the document.
   */
  const embed = useMemo(() => readEmbedParams(), []);
  const embedBridge = useMemo(
    () =>
      embed.isEmbedded ? createEmbedBridge(embed.parentOrigin) : undefined,
    [embed]
  );

  /**
   * Announced on mount rather than once the experience has loaded. The surface
   * uses this to tell "the document is running" from "framing was refused", and
   * a refused frame never gets this far, so waiting for the fetch would only
   * delay the reveal behind the network.
   */
  useEffect(() => {
    embedBridge?.ready();
  }, [embedBridge]);

  /**
   * The overlay has no dismiss control by design, so an experience that fails
   * to load inside it would strand the shopper on an error message. Hand the
   * merchant page the hosted URL instead, which is where the plain redirect
   * integration would have taken them.
   */
  useEffect(() => {
    if (error && embedBridge) {
      embedBridge.navigate(hostedUrl());
    }
  }, [error, embedBridge]);

  const loadExperience = (): void => {
    const pathName = window.location.pathname;
    const splitPath = pathName.split('/');
    const sessionKey = splitPath[2];
    let path: string | undefined;

    const isGeoPath = window.location.pathname.startsWith('/geo/');

    // Handle pricing page session route: /pricing/session/{sessionId} or /geo/pricing/session/{sessionId}
    const pricingPageSession =
      matchPath<{ sessionId: string }>(
        window.location.pathname,
        '/pricing/session/:sessionId'
      ) ||
      matchPath<{ sessionId: string }>(
        window.location.pathname,
        '/geo/pricing/session/:sessionId'
      );
    const sessionId = pricingPageSession?.params.sessionId;

    // Handle pricing page route: /pricing/{pricingPageKey} or /geo/pricing/{pricingPageKey}
    const pricingPage =
      matchPath<{ pricingPageKey: string }>(
        window.location.pathname,
        '/pricing/:pricingPageKey'
      ) ||
      matchPath<{ pricingPageKey: string }>(
        window.location.pathname,
        '/geo/pricing/:pricingPageKey'
      );
    const extractedPricingPageKey = pricingPage?.params.pricingPageKey;

    // Atomic Pricing backward-compat: /sites/{siteId}/pricing/{atomicPricingPageId}
    const atomicPricingPage =
      matchPath<{ siteId: string; atomicPricingPageId: string }>(
        window.location.pathname,
        '/sites/:siteId/pricing/:atomicPricingPageId'
      ) ||
      matchPath<{ siteId: string; atomicPricingPageId: string }>(
        window.location.pathname,
        '/geo/sites/:siteId/pricing/:atomicPricingPageId'
      );

    setPricingPageKey(extractedPricingPageKey);
    setLegacySiteId(undefined);
    setLegacyPricingPageId(undefined);

    const variantIdParam = variantId ? `?variant_id=${variantId}` : '';

    if (sessionId) {
      path = isGeoPath
        ? `/geo/pricing/session/${sessionId}/render${variantIdParam}`
        : `/pricing/session/${sessionId}/render${variantIdParam}`;
      setIsPricingPage(true);
      setPricingPageSessionId(sessionId);
    } else if (extractedPricingPageKey) {
      path = isGeoPath
        ? `/geo/pricing/${extractedPricingPageKey}/render${variantIdParam}`
        : `/pricing/${extractedPricingPageKey}/render${variantIdParam}`;
      setIsPricingPage(true);
      setPricingPageSessionId(undefined);
    } else if (atomicPricingPage) {
      const { siteId, atomicPricingPageId } = atomicPricingPage.params;
      setLegacySiteId(siteId);
      setLegacyPricingPageId(atomicPricingPageId);
      path = isGeoPath
        ? `/geo/sites/${siteId}/pricing/${atomicPricingPageId}/render${variantIdParam}`
        : `/sites/${siteId}/pricing/${atomicPricingPageId}/render${variantIdParam}`;
      setIsPricingPage(true);
      setPricingPageSessionId(undefined);
    } else if (sessionKey) {
      path = `/api/v1/session/${sessionKey}?load_experience`;
    } else if (/preview-experience/.test(pathName)) {
      path = `/api/v1/preview-experience${window.location.search}`;
    }
    if (path) {
      // Embedded only. The overlay has no dismiss control, so a request that
      // stalls rather than fails would leave the visitor stuck behind a frozen
      // card with the merchant page scroll-locked underneath. A hosted page
      // keeps waiting as it always has, since the browser's own chrome is
      // still there to leave with.
      const abort = embedBridge ? new AbortController() : undefined;
      const stalled = abort
        ? setTimeout(() => abort.abort(), EMBEDDED_LOAD_TIMEOUT_MS)
        : undefined;

      fetch(path, { signal: abort?.signal })
        .then(async (resp) => {
          const json = await resp.json();
          if (!resp.ok) {
            throw new Error((json as FailedExperienceResponse).message);
          }
          const data = json as RuntimeExperienceResponse;
          window.bbkClientConfig = data?.config;
          window.bbkFeatureFlags = data?.globalFeatureFlags;
          window.bbkAuthenticityToken = data?.authenticity;

          const geoCurrency = resp.headers.get('X-Geo-Resolved-Currency');
          setDefaultCurrency(geoCurrency || undefined);

          setPricingPageKey(
            data.pricing_page_key ?? extractedPricingPageKey ?? undefined
          );

          setResponse(data);
        })
        .catch((e: APIError) => {
          setError(
            e.message ??
              'An error occurred while loading this page. Please try again later or contact support.'
          );
        })
        .finally(() => clearTimeout(stalled));
    }
  };
  useEffect(() => {
    if (response) {
      const fav = document.getElementById('favicon') as HTMLLinkElement | null;
      if (fav) {
        const title =
          response?.experience?.company?.name ??
          (isPricingPage ? 'Pricing' : 'Cancel');
        const env =
          response.config.environment === 'production'
            ? ''
            : `[${response.config.environment}] `;
        document.title = `${env}${title}`;
        fav.href = response.favicon || '/assets/images/favicon.png';
      }
    }
  }, [response, isPricingPage]);

  useEffect(() => {
    setInterval(loadExperience, 1000 * 60 * 60 * 24); // refresh every 24 hours to keep integrity token, config, and feature flags fresh
    loadExperience();
  }, []);

  // Add full-height class for pricing pages
  useEffect(() => {
    if (!isPricingPage) return;
    const htmlElement = document.documentElement;
    if (htmlElement) {
      htmlElement.classList.add('pricing-page-full-height');
    }
    return () => {
      if (htmlElement) {
        htmlElement.classList.remove('pricing-page-full-height');
      }
    };
  }, [isPricingPage]);

  const centerCss = {
    height: '100vh',
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.1)',
    color: 'rgba(0,0,0,0.6)',
  };
  if (error) {
    return <div css={{ ...centerCss }}>{error}</div>;
  }
  if (!response) {
    return isPricingPage ? (
      <PricingPageShimmerLoader isFullScreen={true} />
    ) : (
      <div css={centerCss}>Loading...</div>
    );
  }

  const experience = response.experience;
  const emotionTheme = resolveFunnelTheme({
    layout: experience?.layout ?? { style_overlay: {} },
    locale: response.locale,
    cardLevelTheme: experience
      ? getPageLoadModalCardTheme(experience.layout, experience.config)
      : undefined,
  });

  return (
    <FunnelContext.Provider
      value={{
        isPricingPage,
        navigate: embedBridge?.navigate ?? navigateInThisWindow,
        pricingPageProps: {
          pricingPageEVId: response?.pricing_page_ev_id ?? 0,
          pricingPageId: response?.pricing_page_id ?? '',
          appId: response?.app_id ?? '',
          siteId: response?.site_id ?? '',
          legacySiteId,
          legacyPricingPageId,
          host,
          pricingPageKey,
          sessionId: pricingPageSessionId,
          customerId: response?.customer_id,
          discounts: response?.discounts,
          subscription: response?.subscription,
          defaultCurrency,
          priceVariantId: variantId,
        },
      }}
    >
      <ThemeProvider theme={theme}>
        <SToastViewport position="bottom-right" />
        <Funnel {...experience} emotionTheme={emotionTheme} />
      </ThemeProvider>
    </FunnelContext.Provider>
  );
};

if (detectIE()) {
  document.querySelector('html')?.classList.add('internetExplorer');
}
renderInlineToDOM(FunnelLoader, {});
